import React, { useState, useEffect } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import Chip from '@mui/material/Chip'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TablePagination from '@mui/material/TablePagination'
import IconButton from '@mui/material/IconButton'
import CircularProgress from '@mui/material/CircularProgress'
import Collapse from '@mui/material/Collapse'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import Timeline from '@mui/lab/Timeline'
import TimelineItem from '@mui/lab/TimelineItem'
import TimelineSeparator from '@mui/lab/TimelineSeparator'
import TimelineConnector from '@mui/lab/TimelineConnector'
import TimelineContent from '@mui/lab/TimelineContent'
import TimelineDot from '@mui/lab/TimelineDot'
import TimelineOppositeContent from '@mui/lab/TimelineOppositeContent'
import InterventionFilters from '../components/InterventionFilters'
import { format, subDays } from 'date-fns'
import { postGeneric } from '../../../common/constants'
import { useGetAllActiveMerchantsIdAndNameCachedQuery } from '../../../redux/commonApiCalls'
import CopyIcon from '../../../common/CopyIcon'

/* infer the intervention channel for a timeline entry from who took the action */
const channelOf = (by) => {
  const s = String(by || '').toUpperCase()
  if (s.includes('FUTWORK') || s.includes('CALL')) return 'Call (Futwork)'
  if (s.includes('WHATSAPP') || s.includes('WA')) return 'WhatsApp'
  if (s.includes('AUTOMAT') || s.includes('RULE') || s.includes('SYSTEM')) return 'Automated Rule'
  if (String(by || '').includes('@')) return 'Manual'
  return 'Manual'
}

/* map a real MongoOrder (with nprData) to the drilldown row shape */
const mapNprOrder = (o, merchants) => {
  const d = o.nprData || {}
  const log = (d.actionUpdateLog || []).map((l) => ({
    actionDate: l.nprCurrentAttemptDate || l.ndrCurrentAttemptDate || l.actionDate || l.createdDate,
    nprAction: l.nprAction || l.ndrAction,
    actionTakenBy: l.actionTakenBy,
    remark: l.remark || l.cpResponse || '',
    channel: channelOf(l.actionTakenBy),
  }))
  const first = log.length ? new Date(log[0].actionDate).getTime() : null
  const reported = d.nprReportedDate ? new Date(d.nprReportedDate).getTime() : null
  const frt = first && reported ? Math.max(0, Math.round((first - reported) / 60000)) : null
  return {
    awbNumber: o.awb_number || o.awbNumber || o.courierDetail?.awbNumber || o.reference || '—',
    merchantName: merchants.find((m) => m.id === o.merchantOid)?.name || o.merchantOid || '—',
    courierName: o.courierDetail?.courierName || o.courierDetail?.parent || o.courierDetail?.cpName || '—',
    nprReportedDate: d.nprReportedDate,
    nprStatus: d.nprStatus,
    currentNprAction: d.currentNprAction,
    remark: d.remark || d.nprCpStatusDescription,
    reAttemptCount: d.reAttemptCount,
    ofPCount: d.ofPCount ?? d.ofpCount,
    nprAge: d.nprAge,
    frt,
    actionUpdateLog: log,
  }
}

const ACTION_COLORS = {
  REATTEMPT: { bg: '#E8F5E9', color: '#2E7D32' },
  CANCEL_PICKUP: { bg: '#FDECEA', color: '#D32F2F' },
  ALREADY_PICKED_UP: { bg: '#E1F5FE', color: '#0288D1' },
  CLOSED: { bg: '#F4F6F8', color: '#637381' },
}

const CHANNEL_DOT_COLORS = {
  'Automated Rule': '#8B5CF6',
  'WhatsApp': '#25D366',
  'Call (Futwork)': '#FF6B35',
  'Manual': '#6B7280',
}

function NprRow({ order }) {
  const [open, setOpen] = useState(false)
  const statusColor = order.nprStatus === 'CLOSED' ? '#2E7D32' : order.nprStatus === 'REPORTED' ? '#ED6C02' : '#0F4C81'

  return (
    <>
      <TableRow hover sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell padding='checkbox'>
          {order.actionUpdateLog?.length > 0 && (
            <IconButton size='small' onClick={() => setOpen(!open)}>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </TableCell>
        <TableCell sx={{ fontWeight: 600, fontFamily: 'monospace', fontSize: '0.8rem' }}>{order.awbNumber}<CopyIcon text={order.awbNumber} /></TableCell>
        <TableCell>{order.merchantName}</TableCell>
        <TableCell>{order.courierName}</TableCell>
        <TableCell>{order.nprReportedDate ? new Date(order.nprReportedDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—'}</TableCell>
        <TableCell align='right'>
          {order.frt != null ? (
            <Typography variant='caption' sx={{ fontWeight: 700, color: order.frt < 60 ? '#2E7D32' : order.frt < 120 ? '#ED6C02' : '#D32F2F' }}>
              {order.frt}m
            </Typography>
          ) : <Typography variant='caption' sx={{ color: 'text.disabled' }}>—</Typography>}
        </TableCell>
        <TableCell>
          <Chip label={order.nprStatus} size='small' sx={{ bgcolor: `${statusColor}14`, color: statusColor, fontWeight: 700 }} />
        </TableCell>
        <TableCell>{order.remark}</TableCell>
        <TableCell align='center'>{order.reAttemptCount}</TableCell>
        <TableCell align='center'>{order.ofPCount}</TableCell>
        <TableCell>
          {order.currentNprAction && (
            <Chip
              label={order.currentNprAction.replace(/_/g, ' ')}
              size='small'
              sx={{
                bgcolor: ACTION_COLORS[order.currentNprAction]?.bg || '#F4F6F8',
                color: ACTION_COLORS[order.currentNprAction]?.color || '#637381',
                fontWeight: 700,
              }}
            />
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={11} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
          <Collapse in={open} timeout='auto' unmountOnExit>
            <Box sx={{ py: 2, pl: 4 }}>
              <Typography variant='caption' sx={{ fontWeight: 700, color: 'text.secondary', mb: 1, display: 'block' }}>
                Action Timeline
              </Typography>
              <Timeline sx={{ p: 0, m: 0 }}>
                {order.actionUpdateLog.map((log, idx) => (
                  <TimelineItem key={idx} sx={{ minHeight: 50, '&::before': { display: 'none' } }}>
                    <TimelineOppositeContent sx={{ flex: 0.15, minWidth: 100, py: 0.5 }}>
                      <Typography variant='caption' sx={{ color: 'text.disabled', fontSize: '0.7rem' }}>
                        {new Date(log.actionDate).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </Typography>
                    </TimelineOppositeContent>
                    <TimelineSeparator>
                      <TimelineDot sx={{ bgcolor: CHANNEL_DOT_COLORS[log.channel] || '#637381', width: 10, height: 10, p: 0 }} />
                      {idx < order.actionUpdateLog.length - 1 && <TimelineConnector />}
                    </TimelineSeparator>
                    <TimelineContent sx={{ py: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Chip label={log.channel} size='small' sx={{ fontSize: '0.68rem', height: 20, bgcolor: `${CHANNEL_DOT_COLORS[log.channel]}18`, color: CHANNEL_DOT_COLORS[log.channel] || '#637381', fontWeight: 700 }} />
                        {log.nprAction && <Chip label={log.nprAction} size='small' variant='outlined' sx={{ fontSize: '0.68rem', height: 20 }} />}
                      </Box>
                      <Typography variant='caption' sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}>
                        {log.remark} {log.actionTakenBy && <span style={{ color: '#919EAB' }}>— {log.actionTakenBy}</span>}
                      </Typography>
                    </TimelineContent>
                  </TimelineItem>
                ))}
              </Timeline>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  )
}

export default function NprDrilldown() {
  const [filters, setFilters] = useState({
    type: 'NPR',
    merchantOids: [],
    dateRange: { from: format(subDays(new Date(), 13), 'yyyy-MM-dd'), to: format(new Date(), 'yyyy-MM-dd') },
  })
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(20)
  const [orders, setOrders] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)

  const { data: merchants = [] } = useGetAllActiveMerchantsIdAndNameCachedQuery()

  // Real data — NPR orders via order/search (nprData), scoped by merchant (date filter
  // omitted: nprData.nprReportedDate needs a backend-specific date format).
  useEffect(() => {
    let on = true
    setLoading(true)
    const andfilter = [{ field: 'nprData.nprStatus', operator: 'in', value: ['INITIATED', 'REQUESTED', 'REPORTED', 'CLOSED'] }]
    if (filters.merchantOids?.length) andfilter.push({ field: 'merchantOid', operator: 'in', value: filters.merchantOids })
    const payload = { offset: page * rowsPerPage, limit: rowsPerPage, skipCount: false, andfilter, includes: 'reference,awb_number,courierDetail,nprData,orderStatus,merchantOid' }
    postGeneric('order/search', payload)
      .then((res) => {
        if (!on) return
        const arr = res?.data?.data || res?.data || []
        setOrders(arr.map((o) => mapNprOrder(o, merchants)))
        setTotal(res?.data?.count ?? arr.length)
      })
      .catch(() => { if (on) { setOrders([]); setTotal(0) } })
      .finally(() => { if (on) setLoading(false) })
    return () => { on = false }
  }, [filters, page, rowsPerPage, merchants])

  return (
    <Box>
      <Typography variant='h5' sx={{ mb: 2 }}>NPR Drilldown</Typography>
      <InterventionFilters filters={filters} onChange={setFilters} />

      <Paper>
        <TableContainer>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell padding='checkbox' />
                <TableCell>AWB</TableCell>
                <TableCell>Merchant</TableCell>
                <TableCell>Courier</TableCell>
                <TableCell>NPR Date</TableCell>
                <TableCell align='right'>FRT</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Remark</TableCell>
                <TableCell align='center'>Reattempts</TableCell>
                <TableCell align='center'>OFP</TableCell>
                <TableCell>Last Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={11} align='center' sx={{ py: 6 }}><CircularProgress size={28} /></TableCell></TableRow>
              )}
              {!loading && orders.map((o, i) => (
                <NprRow key={o.awbNumber + i} order={o} />
              ))}
              {!loading && !orders.length && (
                <TableRow><TableCell colSpan={11} align='center' sx={{ py: 4, color: 'text.disabled' }}>No NPR orders found</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component='div'
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0) }}
          rowsPerPageOptions={[10, 20, 50]}
        />
      </Paper>
    </Box>
  )
}
